/**
 * Seed Custom JS
 * Scroll reveal animations, header transparency toggle, floating pill controller
 */

(function () {
  'use strict';

  // Scroll Reveal with IntersectionObserver.
  // Content is visible by default: the CSS hidden start state only applies
  // under `html.seed-motion`, which is added HERE, and only when we can
  // actually animate. No JS, reduced motion, or a missing observer all leave
  // the page fully visible (see the "hero animate freeze" incident).
  function initScrollReveal() {
    var elements = document.querySelectorAll('.seed-animate');
    if (!elements.length) return;

    var reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // No animation budget / unsupported observer → CSS never hides anything,
    // so there is nothing to do.
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    // Opt in to the hidden start state only now that we know we can reveal.
    document.documentElement.classList.add('seed-motion');

    // Hard-reveal: guarantee the final visible state even if a transition
    // froze mid-flight (can happen on initial paint while the 3D hero blocks
    // the main thread). Temporarily kill the transition so opacity snaps to 1.
    function forceShow(el) {
      var prev = el.style.transition;
      el.style.transition = 'none';
      el.classList.add('is-visible');
      void el.offsetWidth; // flush the no-transition frame
      el.style.transition = prev;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            // Defer one frame so the initial opacity:0 state paints before the
            // transition starts — prevents the reveal freezing at ~0 opacity.
            requestAnimationFrame(function () {
              entry.target.classList.add('is-visible');
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    elements.forEach(function (el) {
      observer.observe(el);
    });

    // Failsafe: nothing must stay invisible. Force-reveal any in-viewport
    // element still below full opacity (frozen transition or missed observer).
    function sweepStuck() {
      elements.forEach(function (el) {
        var bounds = el.getBoundingClientRect();
        var inView =
          bounds.top < (window.innerHeight || document.documentElement.clientHeight) &&
          bounds.bottom > 0;
        if (inView && parseFloat(window.getComputedStyle(el).opacity) < 0.99) {
          forceShow(el);
        }
      });
    }

    // Sweep shortly after init (covers elements already in the viewport whose
    // reveal froze), and again once the page has fully loaded and settled.
    setTimeout(sweepStuck, 1200);
    window.addEventListener('load', function () {
      setTimeout(sweepStuck, 600);
    });
  }

  // Hero parallax: the full-bleed product-hero media (video/poster/image)
  // translates slower than the scroll. GPU transform only, rAF-throttled,
  // clamped to the media's overdraw so it never exposes a gap, and disabled
  // entirely under prefers-reduced-motion (the hero then renders untouched).
  function initHeroParallax() {
    var reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    var heroes = document.querySelectorAll('[data-seed-phero]');
    if (!heroes.length) return;

    var SPEED = 0.14; // fraction of scroll delta applied to the media
    var OVERDRAW = 0.12; // must match .seed-parallax-active top overdraw

    heroes.forEach(function (hero) {
      var media = hero.querySelector('.seed-phero__media');
      if (!media) return;

      hero.classList.add('seed-parallax-active');

      var maxShift = 0;
      var ticking = false;

      function measure() {
        maxShift = hero.offsetHeight * OVERDRAW;
      }

      function update() {
        ticking = false;
        var rect = hero.getBoundingClientRect();
        // Skip work once the hero has fully scrolled out of view.
        if (rect.bottom <= 0) return;
        var shift = Math.min(Math.max(-rect.top * SPEED, 0), maxShift);
        media.style.transform = 'translate3d(0, ' + shift.toFixed(1) + 'px, 0)';
      }

      function request() {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }

      measure();
      update();
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener(
        'resize',
        function () {
          measure();
          request();
        },
        { passive: true }
      );
    });
  }

  // Banner parallax: [data-parallax-banner] media drifts against the scroll
  // (the media layer is over-drawn ±10% by CSS, so the shift never exposes a
  // gap). Vanilla rAF — mirrors the cell-aura GSAP behaviour on pages where
  // cell-aura-scroll.js is not loaded. Skipped entirely under reduced motion.
  function initBannerParallax() {
    var reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    var banners = document.querySelectorAll('[data-parallax-banner]');
    if (!banners.length) return;

    var RANGE = 0.08; // max media shift as a fraction of frame height

    banners.forEach(function (banner) {
      // cell-aura-scroll.js (GSAP) already drives banners on templates that
      // load it; don't double-transform the same node.
      if (window.__cellAuraScrollBooted) return;
      var media = banner.querySelector('[data-parallax-media]');
      if (!media) return;

      var ticking = false;

      function update() {
        ticking = false;
        var rect = banner.getBoundingClientRect();
        var viewH = window.innerHeight || document.documentElement.clientHeight;
        if (rect.bottom <= 0 || rect.top >= viewH) return;
        // -1 (banner below viewport) → +1 (banner above viewport)
        var progress =
          (viewH - rect.top - (viewH + rect.height) / 2) / ((viewH + rect.height) / 2);
        var shift = progress * rect.height * RANGE;
        media.style.transform = 'translate3d(0, ' + shift.toFixed(1) + 'px, 0)';
      }

      function request() {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }

      update();
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request, { passive: true });
    });
  }

  // Testimonials carousel: autoplay + pointer drag + arrows/dots on top of the
  // existing native scroll-snap strip. Touch scrolling stays native.
  function initTestimonialsCarousel() {
    var strips = document.querySelectorAll('[data-seed-carousel]');
    if (!strips.length) return;

    var reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    strips.forEach(function (strip) {
      var section = strip.closest('.seed-testimonials');
      if (!section) return;
      var cards = strip.querySelectorAll('.seed-testimonials__card');
      if (cards.length < 2) return;

      var dots = section.querySelectorAll('[data-seed-tdot]');
      var prevBtn = section.querySelector('[data-seed-tprev]');
      var nextBtn = section.querySelector('[data-seed-tnext]');
      var nav = section.querySelector('.seed-testimonials__nav');

      var AUTOPLAY_MS = 5000;
      var current = 0;
      var timer = null;
      var maxIdx = cards.length - 1;
      var paused = { hover: false, focus: false, pointer: false, hidden: false };

      function step() {
        // Distance between consecutive cards (card width + flex gap).
        return cards.length > 1
          ? cards[1].offsetLeft - cards[0].offsetLeft
          : cards[0].offsetWidth;
      }

      // The last few cards can share the final scroll position, and when every
      // card fits on screen there is nothing to scroll at all. Dots map to
      // REACHABLE positions; surplus dots (and an idle nav) are hidden.
      function measure() {
        var maxScroll = strip.scrollWidth - strip.clientWidth;
        maxIdx =
          maxScroll < 4
            ? 0
            : Math.min(cards.length - 1, Math.round(maxScroll / step()));
        dots.forEach(function (d, idx) {
          d.hidden = idx > maxIdx;
        });
        if (nav) nav.hidden = maxIdx === 0;
      }

      function indexFromScroll() {
        var maxScroll = strip.scrollWidth - strip.clientWidth;
        if (strip.scrollLeft >= maxScroll - 2) return maxIdx;
        var i = Math.round(strip.scrollLeft / step());
        return Math.min(Math.max(i, 0), maxIdx);
      }

      function paint(i) {
        current = i;
        dots.forEach(function (d, idx) {
          var active = idx === i;
          d.classList.toggle('is-active', active);
          if (active) {
            d.setAttribute('aria-current', 'true');
          } else {
            d.removeAttribute('aria-current');
          }
        });
      }

      function goTo(i) {
        var count = maxIdx + 1;
        var target = ((i % count) + count) % count;
        strip.scrollTo({
          left: cards[target].offsetLeft - cards[0].offsetLeft,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
        paint(target);
      }

      // ---- Autoplay ----
      function shouldRun() {
        return (
          !reduceMotion &&
          maxIdx > 0 &&
          !paused.hover &&
          !paused.focus &&
          !paused.pointer &&
          !paused.hidden
        );
      }

      function tick() {
        if (!shouldRun()) return;
        // Only advance while the strip is actually on screen.
        var rect = strip.getBoundingClientRect();
        var viewH = window.innerHeight || document.documentElement.clientHeight;
        if (rect.bottom < 0 || rect.top > viewH) return;
        goTo(current + 1);
      }

      function sync() {
        if (shouldRun()) {
          if (!timer) timer = window.setInterval(tick, AUTOPLAY_MS);
        } else if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
      }

      function pause(reason) {
        paused[reason] = true;
        sync();
      }

      function resume(reason) {
        paused[reason] = false;
        sync();
      }

      function restart() {
        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
        sync();
      }

      section.addEventListener('mouseenter', function () { pause('hover'); });
      section.addEventListener('mouseleave', function () { resume('hover'); });
      section.addEventListener('focusin', function () { pause('focus'); });
      section.addEventListener('focusout', function () {
        if (!section.contains(document.activeElement)) resume('focus');
      });
      strip.addEventListener('touchstart', function () { pause('pointer'); }, { passive: true });
      strip.addEventListener('touchend', function () { resume('pointer'); }, { passive: true });
      document.addEventListener('visibilitychange', function () {
        paused.hidden = document.hidden;
        sync();
      });

      // ---- Active dot tracking on native scroll ----
      var scrollTick = false;
      strip.addEventListener(
        'scroll',
        function () {
          if (scrollTick) return;
          scrollTick = true;
          requestAnimationFrame(function () {
            scrollTick = false;
            paint(indexFromScroll());
          });
        },
        { passive: true }
      );

      // ---- Arrows and dots ----
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          goTo(current - 1);
          restart();
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          goTo(current + 1);
          restart();
        });
      }
      dots.forEach(function (d, idx) {
        d.addEventListener('click', function () {
          goTo(idx);
          restart();
        });
      });

      // ---- Keyboard on the strip itself ----
      strip.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goTo(current - 1);
          restart();
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          goTo(current + 1);
          restart();
        }
      });

      // ---- Mouse drag (touch keeps native scrolling) ----
      var dragging = false;
      var dragMoved = false;
      var dragStartX = 0;
      var dragStartScroll = 0;

      strip.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        dragging = true;
        dragMoved = false;
        dragStartX = e.clientX;
        dragStartScroll = strip.scrollLeft;
        strip.classList.add('is-dragging');
        pause('pointer');
        try {
          strip.setPointerCapture(e.pointerId);
        } catch (err) {
          /* capture unsupported — drag still works */
        }
      });

      strip.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - dragStartX;
        if (Math.abs(dx) > 5) dragMoved = true;
        strip.scrollLeft = dragStartScroll - dx;
      });

      function endDrag() {
        if (!dragging) return;
        dragging = false;
        strip.classList.remove('is-dragging');
        // Settle on the nearest card (snap was suspended while dragging).
        goTo(indexFromScroll());
        resume('pointer');
      }

      strip.addEventListener('pointerup', endDrag);
      strip.addEventListener('pointercancel', endDrag);

      // A real drag must not fire the card's click (e.g. video play button).
      strip.addEventListener(
        'click',
        function (e) {
          if (dragMoved) {
            e.preventDefault();
            e.stopPropagation();
            dragMoved = false;
          }
        },
        true
      );

      window.addEventListener(
        'resize',
        function () {
          measure();
          sync();
        },
        { passive: true }
      );

      measure();
      paint(0);
      sync();
    });
  }

  // Header transparency/solid toggle
  function initHeaderScroll() {
    var header = document.querySelector('.section-header .header-wrapper');
    if (!header) return;

    var isHomepage = document.body.querySelector('.seed-hero') !== null;
    if (!isHomepage) {
      header.classList.remove('seed-header-transparent');
      header.classList.add('seed-header-solid');
      return;
    }

    header.classList.add('seed-header-transparent');

    var scrollThreshold = 100;

    function onScroll() {
      var scrollY = window.pageYOffset || document.documentElement.scrollTop;
      if (scrollY > scrollThreshold) {
        header.classList.remove('seed-header-transparent');
        header.classList.add('seed-header-solid');
      } else {
        header.classList.remove('seed-header-solid');
        header.classList.add('seed-header-transparent');
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // Header dropdown hover logic
  function initHeaderDropdowns() {
    var wrappers = document.querySelectorAll('.seed-header__link-wrapper');
    var panels = document.querySelectorAll('[data-dropdown-panel]');
    var headerLeft = document.querySelector('.seed-header__left');
    var closeTimer = null;

    function closeAll() {
      panels.forEach(function (p) { p.classList.remove('is-open'); });
      wrappers.forEach(function (w) {
        var btn = w.querySelector('[data-dropdown]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    }

    // Anchor the panel under its own trigger rather than the header's left edge,
    // keeping it inside the viewport, and point the caret at the button's centre.
    function positionPanel(panel, btn) {
      if (!headerLeft) return;
      var origin = headerLeft.getBoundingClientRect();
      var button = btn.getBoundingClientRect();
      var width = panel.offsetWidth;
      var gutter = 16;

      var buttonCentre = button.left + button.width / 2;
      var left = buttonCentre - width / 2 - origin.left;
      var min = gutter - origin.left;
      var max = window.innerWidth - gutter - width - origin.left;
      if (max < min) max = min;
      if (left < min) left = min;
      if (left > max) left = max;

      panel.style.left = left + 'px';
      panel.style.setProperty('--caret-x', buttonCentre - origin.left - left + 'px');
    }

    function openPanel(name) {
      clearTimeout(closeTimer);
      closeAll();
      var panel = document.querySelector('[data-dropdown-panel="' + name + '"]');
      var btn = document.querySelector('[data-dropdown="' + name + '"]');
      if (panel && btn) positionPanel(panel, btn);
      if (panel) panel.classList.add('is-open');
      if (btn) btn.setAttribute('aria-expanded', 'true');
    }

    window.addEventListener('resize', closeAll);

    wrappers.forEach(function (wrapper) {
      var btn = wrapper.querySelector('[data-dropdown]');
      if (!btn) return;
      var name = btn.getAttribute('data-dropdown');

      wrapper.addEventListener('mouseenter', function () {
        openPanel(name);
      });

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var panel = document.querySelector('[data-dropdown-panel="' + name + '"]');
        if (panel && panel.classList.contains('is-open')) {
          closeAll();
        } else {
          openPanel(name);
        }
      });
    });

    panels.forEach(function (panel) {
      panel.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
      });
      panel.addEventListener('mouseleave', function () {
        closeTimer = setTimeout(closeAll, 200);
      });
    });

    if (headerLeft) {
      headerLeft.addEventListener('mouseleave', function () {
        closeTimer = setTimeout(closeAll, 200);
      });
      headerLeft.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
      });
    }

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });

    // Close when clicking outside
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.seed-header__left')) {
        closeAll();
      }
    });
  }

  // Header predictive-search overlay
  function initHeaderSearch() {
    var overlay = document.querySelector('[data-seed-search]');
    if (!overlay) return;

    var triggers = document.querySelectorAll('[data-seed-search-trigger]');
    var closeBtn = overlay.querySelector('[data-seed-search-close]');
    var backdrop = overlay.querySelector('[data-seed-search-overlay]');
    var input = overlay.querySelector('input[type="search"]');
    var lastTrigger = null;
    var hideTimer = null;

    function open(trigger) {
      lastTrigger = trigger || null;
      clearTimeout(hideTimer);
      overlay.removeAttribute('hidden');
      // Force reflow so the open transition runs from the hidden state.
      void overlay.offsetWidth;
      overlay.classList.add('is-open');
      document.body.classList.add('seed-search-open');
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      if (input) input.focus();
    }

    function close() {
      overlay.classList.remove('is-open');
      document.body.classList.remove('seed-search-open');
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () {
        if (!overlay.classList.contains('is-open')) overlay.setAttribute('hidden', '');
      }, 350);
      if (lastTrigger) {
        lastTrigger.focus();
        lastTrigger = null;
      }
    }

    triggers.forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        if (overlay.classList.contains('is-open')) {
          close();
        } else {
          open(t);
        }
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });
  }

  // Stagger floating pill animations
  function initFloatingPills() {
    var floaters = document.querySelectorAll('.seed-hero__floating');
    floaters.forEach(function (el, i) {
      el.style.animationDelay = i * 0.7 + 's';
    });
  }

  // 3D Pill parallax on mouse move
  function initPillParallax() {
    var scene = document.querySelector('.pill-scene');
    if (!scene) return;

    var stage = scene.querySelector('.pill-scene__stage');
    if (!stage) return;

    scene.addEventListener('mousemove', function (e) {
      var rect = scene.getBoundingClientRect();
      var x = (e.clientX - rect.left) / rect.width - 0.5;
      var y = (e.clientY - rect.top) / rect.height - 0.5;

      stage.style.transform =
        'translateY(' + (-12 * Math.sin(Date.now() / 1000)) + 'px) ' +
        'rotateY(' + (x * 15) + 'deg) ' +
        'rotateX(' + (-y * 10) + 'deg)';
    });

    scene.addEventListener('mouseleave', function () {
      stage.style.transform = '';
    });
  }

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initScrollReveal();
    initHeroParallax();
    initBannerParallax();
    initTestimonialsCarousel();
    initHeaderScroll();
    initHeaderDropdowns();
    initHeaderSearch();
    initFloatingPills();
    initPillParallax();
  }
})();
